import { useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import type { GridCellEditStartParams, GridColDef, GridValidRowModel, MuiEvent } from '@mui/x-data-grid-premium';
import { useAuthStore } from '../../stores/auth.store';
import { canEditNotes, markNotesStale, normalizeNotes, saveNotes } from './notes';
import { notesColumn } from './notesColumn';

/**
 * The Notes column for an entity grid, and what saving it needs. A grid with its own inline edits calls
 * `saveIfChanged` from its processRowUpdate; one without spreads `gridProps`.
 */
export function useEntityNotes(entity: string) {
  const role = useAuthStore((st) => st.user?.role);
  const queryClient = useQueryClient();
  const editable = canEditNotes(entity, role);
  const { enqueueSnackbar } = useSnackbar();

  const column: GridColDef = useMemo(() => notesColumn({ entity, editable }), [entity, editable]);

  // A keystroke on a selected cell would open the editor emptied, and any click away would save that.
  const onCellEditStart = useCallback((p: GridCellEditStartParams, e: MuiEvent) => {
    if (p.field === 'notes' && p.reason !== 'cellDoubleClick') e.defaultMuiPrevented = true;
  }, []);

  const saveIfChanged = useCallback(async <R extends GridValidRowModel>(next: R, prev: R): Promise<R> => {
    const notes = normalizeNotes(next.notes);
    if (notes === normalizeNotes(prev.notes)) return { ...next, notes: prev.notes };
    await saveNotes(entity, String(prev.id), notes);
    markNotesStale(queryClient, entity);
    return { ...next, notes };
  }, [entity, queryClient]);

  const onProcessRowUpdateError = useCallback((e: unknown) => {
    enqueueSnackbar(`Couldn't save: ${e instanceof Error ? e.message : 'unknown error'}`, { variant: 'error' });
  }, [enqueueSnackbar]);

  return useMemo(() => ({
    column, editable, onCellEditStart, saveIfChanged, onProcessRowUpdateError,
    // Premium's paste writes straight into editable cells, a line per row, past the double-click rule.
    gridProps: { onCellEditStart, processRowUpdate: saveIfChanged, onProcessRowUpdateError, disableClipboardPaste: true },
  }), [column, editable, onCellEditStart, saveIfChanged, onProcessRowUpdateError]);
}
